import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Progress } from "./ui/progress";
import { UploadCloud, FileVideo, FileImage, FileAudio, FileText, X } from "lucide-react";
import { api, formatBytes } from "../lib/api";
import { toast } from "sonner";

const fileIcon = (type = "") => {
  if (type.startsWith("video/")) return FileVideo;
  if (type.startsWith("image/")) return FileImage;
  if (type.startsWith("audio/")) return FileAudio;
  return FileText;
};

export const UploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isReel, setIsReel] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const reset = () => {
    setFiles([]); setTitle(""); setDescription(""); setIsReel(false);
    setProgress(0); setUploading(false);
  };

  const handleFiles = (list) => {
    const arr = Array.from(list);
    setFiles(arr);
    if (arr[0] && !title) setTitle(arr[0].name.replace(/\.[^.]+$/, ""));
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const submit = async () => {
    if (!files.length) return toast.error("Pick a file first");
    setUploading(true); setProgress(8);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const form = new FormData();
        form.append("file", f);
        form.append("title", files.length === 1 ? title || f.name : f.name.replace(/\.[^.]+$/, ""));
        form.append("description", description);
        form.append("is_reel", String(isReel));
        await api.post("/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = Math.min(95, Math.round(((i + (e.loaded / (e.total || 1))) / files.length) * 100));
            setProgress(pct);
          },
        });
      }
      setProgress(100);
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded · faster sync engaged`);
      onUploaded?.();
      setTimeout(() => { onOpenChange(false); reset(); }, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="rounded-sm max-w-2xl border-black/10" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Upload media</DialogTitle>
          <DialogDescription className="text-xs uppercase tracking-[0.2em] text-[#E60000] font-bold">
            Faster sync · 200+ file types · object storage
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`mt-2 border-2 border-dashed rounded-sm cursor-pointer p-10 text-center transition-all ${
            drag ? "border-[#E60000] bg-[#E60000]/5 scale-[1.01]" : "border-black/20 hover:border-black/40"
          }`}
        >
          <UploadCloud className="w-10 h-10 mx-auto text-black/40" strokeWidth={1.5} />
          <p className="mt-3 font-heading font-bold tracking-tight">Drop files or click to browse</p>
          <p className="text-xs text-black/50 mt-1">Video · Image · Audio · Docs · up to 5GB / file</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="upload-file-input"
          />
        </div>

        {files.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-auto">
            {files.map((f, i) => {
              const Icon = fileIcon(f.type);
              return (
                <div key={i} className="flex items-center gap-3 border border-black/10 px-3 py-2 rounded-sm" data-testid={`upload-item-${i}`}>
                  <Icon className="w-4 h-4 text-[#E60000]" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.name}</div>
                    <div className="text-xs text-black/50">{formatBytes(f.size)} · {f.type || "unknown"}</div>
                  </div>
                  {!uploading && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, idx) => idx !== i)); }}
                      data-testid={`remove-file-${i}`}
                    >
                      <X className="w-4 h-4 text-black/50 hover:text-black" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {files.length === 1 && (
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/60">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm mt-1" data-testid="upload-title" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/60">Description</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-sm mt-1 min-h-[60px]" data-testid="upload-description" />
            </div>
            <div className="flex items-center justify-between border border-black/10 px-4 py-3 rounded-sm">
              <div>
                <div className="text-sm font-bold font-heading">Publish as Reel</div>
                <div className="text-xs text-black/50">Vertical 9:16 short for the squad feed</div>
              </div>
              <Switch checked={isReel} onCheckedChange={setIsReel} data-testid="upload-is-reel" />
            </div>
          </div>
        )}

        {uploading && (
          <div className="space-y-1.5">
            <Progress value={progress} className="h-1.5 rounded-none" data-testid="upload-progress" />
            <div className="flex justify-between text-xs">
              <span className="text-[#E60000] font-mono font-bold">{progress}%</span>
              <span className="text-black/50">Synced via Monster Squad CDN</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="upload-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!files.length || uploading}
            className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white"
            data-testid="upload-submit"
          >
            {uploading ? "Syncing…" : `Upload ${files.length || ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
